import { useApi } from "@/api";
import {
    TicketId,
    TicketList,
    TicketListId,
    TicketListUpdateModel,
    TicketListUserPermissionLevels,
} from "@/api/tickets";
import { UserId } from "@/api/users";
import { defineStore } from "pinia";
import { defaultSearchRequest } from "./ticketSearch";
import { TICKET_LIST_KEYS } from "@/queries/ticketLists";
import { useQueryCache } from "@pinia/colada";
import { isDefined } from "@/utils";

export type AddByKeysResult = { added: number; skipped: string[]; missing: string[] };

export const useTicketListsStore = defineStore("ticketLists", {
    actions: {
        async createList(
            model: Omit<TicketList, "id" | "creator" | "created" | "changeLogs">
        ): Promise<TicketList> {
            const { tickets } = useApi();
            const result = await tickets.createList(model);
            await useQueryCache().invalidateQueries({ key: TICKET_LIST_KEYS.stubs() });
            return result;
        },
        async updateList(
            listId: TicketListId,
            model: Omit<TicketListUpdateModel, "listId">
        ): Promise<TicketList> {
            const { tickets } = useApi();
            const result = await tickets.updateList({ listId, ...model });
            const queryCache = useQueryCache();
            await Promise.all([
                queryCache.invalidateQueries({ key: TICKET_LIST_KEYS.detail(listId) }),
                queryCache.invalidateQueries({ key: TICKET_LIST_KEYS.stubs() }),
            ]);
            return result;
        },
        async addTicketsByKeys(listId: TicketListId, keys: string[]): Promise<AddByKeysResult> {
            const { tickets } = useApi();
            const queryCache = useQueryCache();
            const list =
                queryCache.getQueryData<TicketList>(TICKET_LIST_KEYS.detail(listId)) ??
                (await tickets.getList(listId));
            const projectId = list.projectId ?? null;
            const present = new Set(
                list.tickets.map((t) => t.ticket?.key.toUpperCase()).filter(isDefined)
            );
            const ids: TicketId[] = [];
            const skipped: string[] = [];
            const missing: string[] = [];
            for (const key of keys) {
                if (present.has(key)) {
                    skipped.push(key);
                    continue;
                }
                const result = await tickets.search(
                    { ...defaultSearchRequest(), text: key, project: projectId },
                    0,
                    5
                );
                const exact = result.tickets.find((t) => t.key.toUpperCase() === key);
                if (exact) {
                    ids.push(exact.id);
                    present.add(key);
                } else {
                    missing.push(key);
                }
            }
            if (ids.length > 0)
                await this.updateList(listId, { add: ids.map((id) => ({ ticketId: id })) });
            return { added: ids.length, skipped, missing };
        },
        async deleteList(listId: TicketListId) {
            const { tickets } = useApi();
            await tickets.deleteList(listId);
            const queryCache = useQueryCache();
            await queryCache.invalidateQueries({ key: TICKET_LIST_KEYS.stubs() });
            // Mark the dead list stale without refetching it; the view is navigating away.
            await queryCache.invalidateQueries({ key: TICKET_LIST_KEYS.detail(listId) }, false);
        },
        async setListPermission(
            listId: TicketListId,
            userId: UserId,
            permission: TicketListUserPermissionLevels
        ) {
            const { tickets } = useApi();
            const permissions = await tickets.setListPermission(listId, userId, permission);
            useQueryCache().setQueryData(TICKET_LIST_KEYS.permissions(listId), permissions);
        },
        async removeListPermission(listId: TicketListId, userId: UserId) {
            const { tickets } = useApi();
            const permissions = await tickets.removeListPermission(listId, userId);
            useQueryCache().setQueryData(TICKET_LIST_KEYS.permissions(listId), permissions);
        },
    },
});
